import React, { useEffect, useMemo, useState } from 'react'
import { VictoryAxis, VictoryBar, VictoryChart, VictoryStack, VictoryTooltip } from 'victory'

type XValue = number | string | Date
export type StackedBarChartPoint = { x: XValue; y: number; seriesKey?: string; seriesName?: string }
export type StackedBarChartSeries = {
  key: string
  name: string
  color: string
  data: StackedBarChartPoint[]
}

type Props = {
  data: StackedBarChartPoint[]
  series?: StackedBarChartSeries[]
  width?: number
  height?: number
  fillColor?: string
  animate?: boolean
  xTickValues?: XValue[]
  xTickFormat?: ((tick: XValue) => string) | string[]
  yTickFormat?: ((tick: XValue) => string) | string[]
  tooltipFormatter?: (point: StackedBarChartPoint) => string
  skewXAxisLabels?: boolean
}

const getXKey = (value: XValue) => (value instanceof Date ? value.getTime().toString() : String(value))

const compareXValues = (left: XValue, right: XValue) => {
  const leftValue = left instanceof Date ? left.getTime() : typeof left === 'number' ? left : Number(left)
  const rightValue = right instanceof Date ? right.getTime() : typeof right === 'number' ? right : Number(right)

  if (Number.isFinite(leftValue) && Number.isFinite(rightValue)) {
    return leftValue - rightValue
  }

  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
}

const StackedBarChart: React.FC<Props> = ({
  data,
  series,
  width = 960,
  height = 540,
  fillColor = '#009ef7',
  animate = true,
  xTickValues,
  xTickFormat,
  yTickFormat,
  tooltipFormatter,
  skewXAxisLabels = false,
}) => {
  const fontFamily = 'Poppins, Helvetica, sans-serif'
  const gridColor = '#e9ecef'
  const formatTooltip = tooltipFormatter ?? ((point: StackedBarChartPoint) => point.y.toString())
  const [hoveredSeriesKey, setHoveredSeriesKey] = useState<string | null>(null)
  const [selectedSeriesKey, setSelectedSeriesKey] = useState<string | null>(null)
  const visibleSeries = useMemo(() => (series ?? []).filter((seriesItem) => seriesItem.data.length > 0), [series])
  const useMultiSeries = visibleSeries.length > 0
  const activeSeriesKey = hoveredSeriesKey ?? selectedSeriesKey
  const inactiveSeriesOpacity = 0.08
  const inactiveLegendOpacity = 0.25
  const chartPadding = useMemo(
    () => ({ top: 20, right: 48, bottom: skewXAxisLabels ? 96 : 64, left: 82 }),
    [skewXAxisLabels]
  )

  const stackedSeries = useMemo(() => {
    if (!useMultiSeries) {
      return []
    }

    const xValues: XValue[] = []
    const xValueKeys = new Set<string>()
    visibleSeries.forEach((seriesItem) => {
      seriesItem.data.forEach((point) => {
        const key = getXKey(point.x)
        if (!xValueKeys.has(key)) {
          xValueKeys.add(key)
          xValues.push(point.x)
        }
      })
    })

    xValues.sort(compareXValues)

    return visibleSeries.map((seriesItem) => {
      const pointByXKey = new Map(seriesItem.data.map((point) => [getXKey(point.x), point]))
      return {
        ...seriesItem,
        data: xValues.map((xValue) => {
          const point = pointByXKey.get(getXKey(xValue))
          return {
            x: xValue,
            y: point?.y ?? 0,
            seriesKey: seriesItem.key,
            seriesName: seriesItem.name,
          }
        }),
      }
    })
  }, [useMultiSeries, visibleSeries])

  const maxYValue = useMemo(() => {
    if (!useMultiSeries) {
      return Math.max(1, ...data.map((point) => point.y))
    }

    const totalsByXKey = new Map<string, number>()
    stackedSeries.forEach((seriesItem) => {
      seriesItem.data.forEach((point) => {
        const key = getXKey(point.x)
        totalsByXKey.set(key, (totalsByXKey.get(key) ?? 0) + point.y)
      })
    })

    return Math.max(1, ...Array.from(totalsByXKey.values()))
  }, [data, stackedSeries, useMultiSeries])
  const xValueCount = useMemo(
    () => (useMultiSeries ? stackedSeries[0]?.data.length ?? 0 : data.length),
    [data.length, stackedSeries, useMultiSeries]
  )
  const barWidth = useMemo(() => {
    const plotWidth = Math.max(120, width - chartPadding.left - chartPadding.right)
    const slotWidth = plotWidth / Math.max(xValueCount, 1)
    return Math.max(6, Math.min(36, slotWidth * 0.62))
  }, [chartPadding.left, chartPadding.right, width, xValueCount])
  const xDomainPadding = Math.ceil(barWidth / 2) + 14
  const normalizedXTickValues = useMemo(() => {
    const values = Array.isArray(xTickValues) ? xTickValues : []
    if (values.length === 0) {
      return undefined
    }

    return Array.from(new Map(values.map((value) => [getXKey(value), value])).values()).sort(compareXValues)
  }, [xTickValues])

  useEffect(() => {
    if (!useMultiSeries) {
      setSelectedSeriesKey(null)
      return
    }

    setSelectedSeriesKey((currentSeriesKey) => {
      if (currentSeriesKey && visibleSeries.some((seriesItem) => seriesItem.key === currentSeriesKey)) {
        return currentSeriesKey
      }

      return null
    })
  }, [useMultiSeries, visibleSeries])

  return (
    <>
      <VictoryChart
        animate={animate ? { duration: 1000, onLoad: { duration: 1 } } : false}
        width={width}
        height={height}
        padding={chartPadding}
        domain={{ y: [0, maxYValue] }}
        domainPadding={{ x: [xDomainPadding, xDomainPadding], y: [0, 24] }}
      >
        {useMultiSeries ? (
          <VictoryStack>
            {stackedSeries.map((seriesItem) => (
              <VictoryBar
                key={seriesItem.key}
                alignment='middle'
                barWidth={barWidth}
                labels={({ datum }) => formatTooltip(datum as StackedBarChartPoint)}
                labelComponent={
                  <VictoryTooltip
                    style={{ fontFamily: fontFamily, fontSize: 10 }}
                    flyoutStyle={{ stroke: '#dfe2e6', fill: '#fff' }}
                  />
                }
                events={[
                  {
                    target: 'data',
                    eventHandlers: {
                      onClick: () => {
                        setSelectedSeriesKey(seriesItem.key)
                        return []
                      },
                    },
                  },
                ]}
                style={{
                  data: {
                    fill: seriesItem.color,
                    opacity: activeSeriesKey === null || activeSeriesKey === seriesItem.key ? 1 : inactiveSeriesOpacity,
                  },
                }}
                data={seriesItem.data}
              />
            ))}
          </VictoryStack>
        ) : (
          <VictoryBar
            style={{ data: { fill: fillColor } }}
            alignment='middle'
            barWidth={barWidth}
            labels={({ datum }) => formatTooltip(datum as StackedBarChartPoint)}
            labelComponent={
              <VictoryTooltip
                style={{ fontFamily: fontFamily, fontSize: 10 }}
                flyoutStyle={{ stroke: '#dfe2e6', fill: '#fff' }}
              />
            }
            data={data}
          />
        )}
        <VictoryAxis
          tickValues={normalizedXTickValues}
          tickFormat={xTickFormat}
          fixLabelOverlap={false}
          style={{
            tickLabels: {
              fontFamily: fontFamily,
              fontSize: 11,
              padding: skewXAxisLabels ? 18 : 8,
              angle: skewXAxisLabels ? -40 : 0,
              textAnchor: skewXAxisLabels ? 'end' : 'middle',
            },
          }}
          animate={false}
        />
        <VictoryAxis
          dependentAxis
          animate={false}
          tickFormat={yTickFormat}
          style={{
            tickLabels: { fontFamily: fontFamily, fontSize: 12 },
            grid: { stroke: gridColor, strokeDasharray: '10,5' },
          }}
        />
      </VictoryChart>
      {useMultiSeries && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            marginTop: 0,
            paddingBottom: 8,
          }}
        >
          {visibleSeries.map((seriesItem) => (
            <button
              key={seriesItem.key}
              type='button'
              onClick={() => setSelectedSeriesKey(seriesItem.key)}
              onMouseEnter={() => setHoveredSeriesKey(seriesItem.key)}
              onMouseLeave={() => setHoveredSeriesKey(null)}
              onFocus={() => setHoveredSeriesKey(seriesItem.key)}
              onBlur={() => setHoveredSeriesKey(null)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                border: '1px solid #dbe2e8',
                borderRadius: 999,
                padding: '6px 10px',
                background: 'transparent',
                color: '#3f4254',
                fontSize: 12,
                cursor: 'pointer',
                opacity: activeSeriesKey === null || activeSeriesKey === seriesItem.key ? 1 : inactiveLegendOpacity,
                borderColor: selectedSeriesKey === seriesItem.key ? seriesItem.color : '#dbe2e8',
                transition: 'opacity 0.15s ease, border-color 0.15s ease',
              }}
              title={seriesItem.name}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 999,
                  background: seriesItem.color,
                  display: 'inline-block',
                }}
              />
              <span>{seriesItem.name}</span>
            </button>
          ))}
        </div>
      )}
    </>
  )
}

export { StackedBarChart }
